import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { HiMagnifyingGlass, HiXMark } from "react-icons/hi2";
import { HiExternalLink } from "react-icons/hi";
import { FiCopy } from "react-icons/fi";

import Loader from "../../components/loader";
import api from "../../services/api";

export default function List() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState(null);
  const [filteredDeals, setFilteredDeals] = useState(null);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [viewMode, setViewMode] = useState("kanban"); // "list" | "kanban"
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState("overview"); // "overview" | "raw"

  useEffect(() => {
    get();
  }, []);

  useEffect(() => {
    if (!deals) return;
    
    let filtered = [...deals];
    
    // Apply search filter
    if (search) {
      filtered = filtered.filter((deal) =>
        deal.properties.dealname?.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    // Apply stage filter
    if (stageFilter) {
      filtered = filtered.filter((deal) => deal.properties.dealstage === stageFilter);
    }
    
    setFilteredDeals(filtered);
  }, [search, stageFilter, deals]);

  const get = async () => {
    try {
      setLoading(true);
      const response = await api.get("/hubspot_deals");
      if (!response.ok) {
        toast.error(response.code || "Error fetching deals!");
        setDeals([]);
        setFilteredDeals([]);
        return;
      }
      setDeals(response.data || []);
      setFilteredDeals(response.data || []);
    } catch (error) {
      console.log(error);
      toast.error("Error fetching deals!");
      setDeals([]);
      setFilteredDeals([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStageLabel = (stage) => {
    const stages = {
      // Custom pipeline stages
      "1238104209": "Lead créé",
      "1238104210": "Discovery complète",

      // Standard HubSpot stages
      qualifiedtobuy: "Qualified",
      appointmentscheduled: "Appointment",
      presentationscheduled: "Presentation",
      decisionmakerboughtin: "Decision Maker",
      contractsent: "Contract Sent",
      closedwon: "Won",
      closedlost: "Lost",
    };
    return stages[stage] || stage;
  };

  const getStageColor = (stage) => {
    const colors = {
      "1238104209": "bg-slate-100 text-slate-800",
      "1238104210": "bg-sky-100 text-sky-800",
      qualifiedtobuy: "bg-blue-100 text-blue-800",
      appointmentscheduled: "bg-purple-100 text-purple-800",
      presentationscheduled: "bg-yellow-100 text-yellow-800",
      decisionmakerboughtin: "bg-orange-100 text-orange-800",
      contractsent: "bg-indigo-100 text-indigo-800",
      closedwon: "bg-green-100 text-green-800",
      closedlost: "bg-red-100 text-red-800",
    };
    return colors[stage] || "bg-gray-100 text-gray-800";
  };

  const getUniqueStages = () => {
    const source = filteredDeals || deals;
    if (!source) return [];
    const stages = [...new Set(source.map((deal) => deal.properties.dealstage))].filter(Boolean);

    const orderedStages = [
      "1238104209", // Lead créé
      "appointmentscheduled", // Premier contact établi
      "1238104210", // Discovery complète
      "qualifiedtobuy", // Besoin qualifié
      "presentationscheduled", // Proposition envoyée
      "decisionmakerboughtin", // Négociation
      "contractsent", // Signature en cours
      "closedwon", // Closed won
      "closedlost", // Closed lost
    ];

    return stages.sort((a, b) => {
      const ia = orderedStages.indexOf(a);
      const ib = orderedStages.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  };

  const calculateTotalValue = () => {
    if (!filteredDeals) return 0;
    return filteredDeals.reduce((sum, deal) => {
      const amount = parseFloat(deal.properties.amount || 0);
      return sum + amount;
    }, 0);
  };

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      toast.success("Copied to clipboard");
    } catch (e) {
      console.error(e);
      toast.error("Unable to copy");
    }
  };

  const openDealModal = async (deal) => {
    setSelectedDeal(deal);
    setIsModalOpen(true);
    setModalTab("overview");
    setDetails(null);
    setDetailsError(null);
    setDetailsLoading(true);

    try {
      const response = await api.get(`/hubspot_deals/${deal.id}/details`);
      if (!response.ok) {
        setDetailsError(response.code || "Error fetching deal details");
        toast.error(response.code || "Error fetching deal details");
        return;
      }
      setDetails(response.data);
    } catch (error) {
      console.error(error);
      setDetailsError("Error fetching deal details");
      toast.error("Error fetching deal details");
    } finally {
      setDetailsLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">HubSpot Deals</h2>
          <p className="text-sm text-gray-500 mt-1">
            {filteredDeals?.length || 0} deals • Total value: {formatCurrency(calculateTotalValue())}
          </p>
        </div>
        <div className="inline-flex rounded-md shadow-sm border border-gray-200 bg-white overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => setViewMode("list")}
            className={`px-3 py-1.5 font-medium ${
              viewMode === "list" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            List
          </button>
          <button
            type="button"
            onClick={() => setViewMode("kanban")}
            className={`px-3 py-1.5 font-medium border-l border-gray-200 ${
              viewMode === "kanban" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            Kanban
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <HiMagnifyingGlass className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Search deals by name..."
          />
        </div>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
          <option value="">All Stages</option>
          {getUniqueStages().map((stage) => (
            <option key={stage} value={stage}>
              {getStageLabel(stage)}
            </option>
          ))}
        </select>
        {(search || stageFilter) && (
          <button
            onClick={() => {
              setSearch("");
              setStageFilter("");
            }}
            className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Reset
          </button>
        )}
      </div>

      {/* List / Kanban content */}
      {viewMode === "list" ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deal Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Close Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDeals && filteredDeals.length > 0 ? (
                  filteredDeals.map((deal) => (
                    <tr key={deal.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {deal.properties.dealname || "Untitled Deal"}
                        </div>
                        <div className="text-xs text-gray-500">ID: {deal.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(deal.properties.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(
                            deal.properties.dealstage
                          )}`}>
                          {getStageLabel(deal.properties.dealstage)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(deal.properties.closedate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(deal.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => openDealModal(deal)}
                            className="text-xs text-gray-700 border border-gray-300 rounded px-2 py-1 hover:bg-gray-50"
                          >
                            Details
                          </button>
                          <a
                            href={deal.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
                            HubSpot <HiExternalLink className="h-4 w-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500">
                      No deals found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {(() => {
            if (!filteredDeals || filteredDeals.length === 0) {
              return (
                <div className="text-sm text-gray-500 px-2">No deals found</div>
              );
            }

            const columns = filteredDeals.reduce((acc, deal) => {
              const stage = deal.properties.dealstage || "Uncategorized";
              if (!acc[stage]) acc[stage] = [];
              acc[stage].push(deal);
              return acc;
            }, {});

            const stages = getUniqueStages();

            return stages.map((stage) => {
              const stageDeals = columns[stage] || [];
              return (
                <div key={stage} className="min-w-[260px] max-w-xs bg-gray-50 border border-gray-200 rounded-lg flex flex-col">
                  <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between bg-gray-100">
                    <span className="text-xs font-semibold uppercase text-gray-600">
                      {getStageLabel(stage)}
                    </span>
                    <span className="text-xs text-gray-500">{stageDeals.length}</span>
                  </div>
                  <div className="p-2 space-y-2 overflow-y-auto max-h-[70vh]">
                    {stageDeals.length === 0 ? (
                      <div className="text-xs text-gray-400 text-center py-4">No deals</div>
                    ) : (
                      stageDeals.map((deal) => (
                        <div
                          key={deal.id}
                          className="bg-white rounded-md border border-gray-200 shadow-sm p-3 space-y-1 hover:border-blue-500 transition-colors cursor-pointer"
                          onClick={() => openDealModal(deal)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-sm font-medium text-gray-900 line-clamp-2">
                              {deal.properties.dealname || "Untitled Deal"}
                            </div>
                            <a
                              href={deal.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 text-blue-500 hover:text-blue-700"
                            >
                              <HiExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                            <span className="font-semibold text-gray-900">
                              {formatCurrency(deal.properties.amount)}
                            </span>
                            <span>{formatDate(deal.properties.closedate) || "No close date"}</span>
                          </div>
                          <div className="text-[11px] text-gray-400 mt-1">
                            Created {formatDate(deal.createdAt)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {isModalOpen && selectedDeal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedDeal.properties.dealname || "Untitled Deal"}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {formatCurrency(selectedDeal.properties.amount)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-700 text-sm p-1 rounded-full hover:bg-gray-100"
                aria-label="Close"
              >
                <HiXMark className="h-4 w-4" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="inline-flex rounded-md shadow-sm border border-gray-200 bg-white overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setModalTab("overview")}
                  className={`px-3 py-1.5 font-medium ${
                    modalTab === "overview" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  Overview
                </button>
                <button
                  type="button"
                  onClick={() => setModalTab("raw")}
                  className={`px-3 py-1.5 font-medium border-l border-gray-200 ${
                    modalTab === "raw" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  Raw
                </button>
              </div>

              {modalTab === "overview" ? (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Deal summary</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-xs text-gray-500">Amount</div>
                          <div className="font-semibold text-gray-900">
                            {formatCurrency(selectedDeal.properties.amount)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Stage</div>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(
                              selectedDeal.properties.dealstage
                            )}`}
                          >
                            {getStageLabel(selectedDeal.properties.dealstage)}
                          </span>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Pipeline</div>
                          <div className="text-gray-900">
                            {selectedDeal.properties.pipeline || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Close date</div>
                          <div className="text-gray-900">
                            {formatDate(selectedDeal.properties.closedate)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Created</div>
                          <div className="text-gray-900">
                            {formatDate(selectedDeal.createdAt)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Last modified</div>
                          <div className="text-gray-900">
                            {formatDate(selectedDeal.properties.hs_lastmodifieddate)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end pt-2 border-t border-dashed border-gray-200 mt-2">
                        <a
                          href={selectedDeal.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          Open in HubSpot <HiExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>

                    <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Owner</h4>
                      {detailsLoading && <div className="text-xs text-gray-500">Loading details...</div>}
                      {detailsError && <div className="text-xs text-red-500">{detailsError}</div>}
                      {!detailsLoading && !detailsError && details?.owner && (
                        <div className="text-sm text-gray-900 space-y-1">
                          <div className="font-medium">
                            {details.owner.firstName} {details.owner.lastName}
                          </div>
                          {details.owner.email && (
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                              <span>{details.owner.email}</span>
                              <button
                                type="button"
                                onClick={() => copyToClipboard(details.owner.email)}
                                className="p-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                                aria-label="Copy owner email"
                              >
                                <FiCopy className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {!detailsLoading && !detailsError && !details?.owner && (
                        <div className="text-xs text-gray-500">No owner information</div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Contacts</h4>
                      {detailsLoading && <div className="text-xs text-gray-500">Loading contacts...</div>}
                      {!detailsLoading && details?.contacts && details.contacts.length > 0 ? (
                        <ul className="space-y-2 text-sm text-gray-900">
                          {details.contacts.map((contact) => (
                            <li key={contact.id} className="flex flex-col gap-0.5">
                              <span className="font-medium">
                                {contact.properties?.firstname} {contact.properties?.lastname}
                              </span>
                              {contact.properties?.email && (
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <span>{contact.properties.email}</span>
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(contact.properties.email)}
                                    className="p-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                                    aria-label="Copy contact email"
                                  >
                                    <FiCopy className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        !detailsLoading && <div className="text-xs text-gray-500">No contacts</div>
                      )}
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Companies</h4>
                      {detailsLoading && <div className="text-xs text-gray-500">Loading companies...</div>}
                      {!detailsLoading && details?.companies && details.companies.length > 0 ? (
                        <ul className="space-y-2 text-sm text-gray-900">
                          {details.companies.map((company) => (
                            <li key={company.id} className="flex flex-col">
                              <span className="font-medium">{company.properties?.name}</span>
                              {company.properties?.domain && (
                                <span className="text-xs text-gray-500">{company.properties.domain}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        !detailsLoading && <div className="text-xs text-gray-500">No companies</div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-[60vh]">
                  <pre className="whitespace-pre-wrap break-all">
                    {JSON.stringify(
                      {
                        selectedDeal,
                        details,
                      },
                      null,
                      2
                    )}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
